import { useEffect, useMemo } from 'react'
import type { JSX } from 'react'
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom'

import {
  Button,
  CopyIcon,
  EmptyState,
  Notice,
  PageLayout,
  VisuallyHidden,
} from '@buffer-mono/popcorn'

import { TemplateCard } from '~publish/components/TemplateCard'
import {
  normalizePathname,
  PUBLIC_PUBLISH_ROUTE_PREFIX,
} from '~publish/helpers/publicPublishPaths'
import { usePageTitle } from '~publish/hooks/usePageTitle'
import { createTemplates, newPost } from '~publish/legacy/routes'

import { CreateTabsActions } from '../../../CreateHeaderSlots'
import { CreatePageLayout } from '../../../CreatePageLayout'
import type { CreatePageTab } from '../../../CreatePageLayout'
import { GOALS, TYPES } from '../../types'
import { usePublicPostTemplates } from '../../hooks/usePublicPostTemplates'
import type { PublicPostTemplate } from '../../hooks/usePublicPostTemplates'
import { useTemplateSearch } from '../../hooks/useTemplateSearch'
import {
  LibraryTemplateGrid,
  NoTemplatesFound,
  TemplateSection,
  TemplateSections,
  TemplatesLoadingGrid,
  TemplatesSearchAndFilter,
  toTemplateFilterOptions,
} from '../TemplateLibraryPage/TemplateLibraryParts'
import type { TemplateFilterGroup } from '../TemplateLibraryPage/TemplateLibraryParts'
import {
  TemplatePreviewDialog,
  useCopyTemplateBody,
} from '../Template/TemplatePreviewDialog'

import styles from './PublicTemplateLibraryPage.module.css'

export const PUBLIC_TEMPLATE_LIBRARY_PATH = `${PUBLIC_PUBLISH_ROUTE_PREFIX}${createTemplates.route}`
const PUBLIC_NEW_POST_PATH = `${PUBLIC_PUBLISH_ROUTE_PREFIX}${newPost.route}`
const PUBLIC_TEMPLATE_DETAILS_PATH = `${PUBLIC_TEMPLATE_LIBRARY_PATH}/template/`
const PUBLIC_TEMPLATE_DETAILS_NAVIGATION_STATE = 'public-template-library'
const PUBLIC_CREATE_TABS: CreatePageTab[] = [
  { value: 'templates', label: 'Templates', to: PUBLIC_TEMPLATE_LIBRARY_PATH },
]
const VALID_FILTERS = new Set<string>([...TYPES, ...GOALS])
const PUBLIC_FILTER_GROUPS: TemplateFilterGroup[] = [
  {
    heading: 'Type',
    filterType: 'category',
    options: toTemplateFilterOptions(TYPES),
  },
  {
    heading: 'Goal',
    filterType: 'goal',
    options: toTemplateFilterOptions(GOALS),
  },
]

function getTemplateIdFromPath(pathname: string): string | null {
  const normalizedPathname = normalizePathname(pathname)
  if (!normalizedPathname.startsWith(PUBLIC_TEMPLATE_DETAILS_PATH)) return null

  const encodedId = normalizedPathname.slice(
    PUBLIC_TEMPLATE_DETAILS_PATH.length,
  )
  if (!encodedId || encodedId.includes('/')) return null

  try {
    return decodeURIComponent(encodedId)
  } catch {
    return null
  }
}

function PublicTemplatePreview({
  template,
  onOpenChange,
  onUseTemplate,
}: {
  template: PublicPostTemplate | undefined
  onOpenChange: (open: boolean) => void
  onUseTemplate: (templateId: string) => void
}): JSX.Element {
  const copyTemplateBody = useCopyTemplateBody(template?.body)

  return (
    <TemplatePreviewDialog
      template={template}
      open={Boolean(template)}
      onOpenChange={onOpenChange}
      actions={
        template ? (
          <>
            <Button
              variant="secondary"
              size="medium"
              onClick={copyTemplateBody}
            >
              <CopyIcon />
              Copy template
            </Button>
            <Button size="medium" onClick={() => onUseTemplate(template.id)}>
              Use Template
            </Button>
          </>
        ) : null
      }
    />
  )
}

function LoadingGrid(): JSX.Element {
  return (
    <>
      <VisuallyHidden as="output">Loading templates</VisuallyHidden>
      <div aria-hidden="true">
        <TemplatesLoadingGrid />
      </div>
    </>
  )
}

export function PublicTemplateLibraryPage(): JSX.Element {
  usePageTitle('Templates')

  const location = useLocation()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { loading, templates, error } = usePublicPostTemplates()

  const query = searchParams.get('query') ?? ''
  const filters = searchParams
    .getAll('filters')
    .filter((filter) => VALID_FILTERS.has(filter))
  const filteredTemplates = useTemplateSearch({
    templates,
    query,
    filters,
    activeCampaigns: [],
  })
  const hasTemplates = templates.length > 0
  const hasPartialError = Boolean(error && hasTemplates)
  const isBrowsing = !query && filters.length === 0
  const sections = useMemo(
    () =>
      TYPES.map((type) => ({
        type,
        templates: templates.filter((template) => template.type === type),
      })).filter((section) => section.templates.length > 0),
    [templates],
  )
  const showsSections = isBrowsing && sections.length > 0

  const selectedTemplateId = getTemplateIdFromPath(location.pathname)
  const selectedTemplate = useMemo(
    () => templates.find((template) => template.id === selectedTemplateId),
    [selectedTemplateId, templates],
  )

  useEffect(() => {
    if (loading || error || !selectedTemplateId || selectedTemplate) return

    navigate(
      {
        pathname: PUBLIC_TEMPLATE_LIBRARY_PATH,
        search: location.search,
      },
      { replace: true },
    )
  }, [
    error,
    loading,
    location.search,
    navigate,
    selectedTemplateId,
    selectedTemplate,
  ])

  const setQuery = (nextQuery: string): void => {
    const next = new URLSearchParams(searchParams)
    if (nextQuery) next.set('query', nextQuery)
    else next.delete('query')
    setSearchParams(next, { replace: true })
  }

  const setFilters = (nextFilters: string[]): void => {
    const next = new URLSearchParams(searchParams)
    next.delete('filters')
    nextFilters.forEach((filter) => next.append('filters', filter))
    setSearchParams(next, { replace: true })
  }

  const resetSearch = (): void => {
    const next = new URLSearchParams(searchParams)
    next.delete('query')
    next.delete('filters')
    setSearchParams(next, { replace: true })
  }

  const closePreview = (open: boolean): void => {
    if (open) return

    if (location.state === PUBLIC_TEMPLATE_DETAILS_NAVIGATION_STATE) {
      navigate(-1)
    } else {
      navigate(
        {
          pathname: PUBLIC_TEMPLATE_LIBRARY_PATH,
          search: location.search,
        },
        { replace: true },
      )
    }
    if (selectedTemplateId) {
      requestAnimationFrame(() => {
        document
          .getElementById(`public-template-card-${selectedTemplateId}`)
          ?.focus()
      })
    }
  }

  const renderTemplateCard = (template: PublicPostTemplate): JSX.Element => (
    <TemplateCard key={template.id} template={template}>
      <TemplateCard.Clickable
        asChild
        id={`public-template-card-${template.id}`}
      >
        <Link
          to={{
            pathname: `${PUBLIC_TEMPLATE_DETAILS_PATH}${encodeURIComponent(template.id)}`,
            search: location.search,
          }}
          state={PUBLIC_TEMPLATE_DETAILS_NAVIGATION_STATE}
          aria-label={`Open template ${template.title}`}
        />
      </TemplateCard.Clickable>
      <TemplateCard.Icon />
      <TemplateCard.Title as={showsSections ? 'h3' : 'h2'} />
      <TemplateCard.Description />
      <TemplateCard.Tags />
    </TemplateCard>
  )

  return (
    <CreatePageLayout currentTab="templates" tabs={PUBLIC_CREATE_TABS}>
      <CreateTabsActions>
        <TemplatesSearchAndFilter
          query={query}
          filters={filters}
          groups={PUBLIC_FILTER_GROUPS}
          showFilters
          cta="create-templates-header-filter-1"
          onQueryChange={setQuery}
          onFiltersChange={setFilters}
          onReset={resetSearch}
        />
      </CreateTabsActions>
      <PageLayout>
        <PageLayout.Container className={styles.content}>
          {loading ? <LoadingGrid /> : null}
          {!loading && error && !hasTemplates ? (
            <EmptyState size="large" className={styles.emptyState} role="alert">
              <EmptyState.Heading>Templates are unavailable</EmptyState.Heading>
              <EmptyState.Description>
                Please try again in a moment.
              </EmptyState.Description>
            </EmptyState>
          ) : null}
          {!loading && hasPartialError ? (
            <Notice variant="warning" className={styles.partialError}>
              Some templates could not be loaded. Showing the templates that are
              available.
            </Notice>
          ) : null}
          {!loading && (!error || hasTemplates) ? (
            showsSections ? (
              <TemplateSections>
                {sections.map((section) => (
                  <TemplateSection
                    key={section.type}
                    title={section.type}
                    templates={section.templates}
                    onSeeAll={() => setFilters([section.type])}
                  >
                    {renderTemplateCard}
                  </TemplateSection>
                ))}
              </TemplateSections>
            ) : (
              <LibraryTemplateGrid
                templates={filteredTemplates}
                emptyState={
                  <>
                    <VisuallyHidden as="output">
                      No templates found
                    </VisuallyHidden>
                    <NoTemplatesFound clearFilters={resetSearch} />
                  </>
                }
              >
                {renderTemplateCard}
              </LibraryTemplateGrid>
            )
          ) : null}
        </PageLayout.Container>
      </PageLayout>
      <PublicTemplatePreview
        template={selectedTemplate}
        onOpenChange={closePreview}
        onUseTemplate={(templateId): void => {
          const nextSearchParams = new URLSearchParams({ templateId })
          navigate({
            pathname: PUBLIC_NEW_POST_PATH,
            search: nextSearchParams.toString(),
          })
        }}
      />
    </CreatePageLayout>
  )
}
